import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Upload } from "lucide-react";
import { toast } from "sonner";

export function PgnImportModal({ open, onClose, onImport }) {
  const [playerColor, setPlayerColor] = useState("white");
  const [opponentName, setOpponentName] = useState("Imported Opponent");
  const [pgnText, setPgnText] = useState("");
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(false);
  }, [open]);

  const handleLoadFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const text = await file.text();
    setPgnText(text);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onImport({
        pgn: pgnText,
        player_color: playerColor,
        opponent_name: opponentName,
      });
      setPgnText("");
      onClose(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.message || "Failed to import PGN");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl" data-testid="import-pgn-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-white">Import PGN</DialogTitle>
          <DialogDescription className="text-white/50">
            Paste a PGN or upload a `.pgn` file to resume a previous session.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white/70 text-sm">Resume As</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPlayerColor("white")}
                  className={`border-white/10 ${playerColor === "white" ? "bg-white/10 text-white" : "text-white/60"}`}
                >
                  White
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPlayerColor("black")}
                  className={`border-white/10 ${playerColor === "black" ? "bg-white/10 text-white" : "text-white/60"}`}
                >
                  Black
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white/70 text-sm">Opponent Name</Label>
              <Input
                value={opponentName}
                onChange={(e) => setOpponentName(e.target.value)}
                className="bg-transparent border-white/20 text-white"
                placeholder="Imported Opponent"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pgn,text/plain"
              onChange={handleLoadFile}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="border-white/10 text-white/70 hover:bg-white/5"
            >
              <Upload size={14} className="mr-2" />
              Load PGN File
            </Button>
            <span className="text-xs text-white/40">or paste below</span>
          </div>

          <div className="space-y-2">
            <Label className="text-white/70 text-sm">PGN Text</Label>
            <Textarea
              value={pgnText}
              onChange={(e) => setPgnText(e.target.value)}
              rows={14}
              placeholder='[Event "Casual Game"] ...'
              className="bg-transparent border-white/20 text-white font-mono text-xs min-h-[260px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onClose(false)} className="border-white/10 text-white/70">
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={loading || !pgnText.trim()}
            className="bg-[#D4AF37] text-black hover:bg-[#E5C158]"
          >
            {loading ? "Importing..." : "Import Game"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
